import {
  agendaSchema,
  registrationViewSchema,
  type Agenda,
  type Availability,
  type EditionSummary,
  type RegistrationPerson,
} from '@koderek/shared';
import {
  Alert,
  Anchor,
  Button,
  Center,
  Collapse,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
  Title,
  Typography,
  VisuallyHidden,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { ApiError, apiGet } from '../api/client';
import { ErrorAlert } from '../components/ErrorAlert';
import { IconChevronDown, IconClock, IconExternalLink, IconFileText, IconFloorPlan, IconInfoCircle, IconMapPin, IconRoute } from '../components/icons';
import { ActivityDetail } from '../features/agenda/ActivityDetail';
import { AgendaView } from '../features/agenda/AgendaView';
import { useMe } from '../features/auth/session';
import { readActivePerson, saveActivePerson } from '../features/editions/activePerson';
import { calendarParts, editionPlaceLabel, formatDate, formatDateTime, formatTime } from '../features/editions/format';
import headerClasses from './EditionHeader.module.css';
import { useEdition, useEditions } from '../features/editions/queries';
import { PersonPlan } from '../features/enrollment/PersonPlan';
import { useAvailability, useEnrollment, withAvailability, type EnrollmentControls } from '../features/enrollment/useEnrollment';
import { PersonTabs } from '../features/participants/PersonTabs';
import { t } from '../texts/pl';
import { NotFoundPage } from './NotFoundPage';

/**
 * US-104 AC-4 stały adres edycji, US-301 agenda, US-302 szczegóły zajęć (`/edycje/{edycja}/zajecia/{symbol}` – na komputerze
 * okno nad agendą, na telefonie osobny widok), US-408 przełącznik osób i plan, US-501…US-507 zapisy z liczbą miejsc na żywo.
 */
export function EditionPage() {
  const { slug = '', symbol } = useParams();
  const navigate = useNavigate();
  const edition = useEdition(slug);
  const allEditions = useEditions();
  const me = useMe().data;
  const agenda = useQuery({
    queryKey: ['agenda', slug, me?.id ?? null],
    queryFn: () => apiGet(`/editions/${encodeURIComponent(slug)}/agenda`, agendaSchema),
    enabled: edition.isSuccess,
  });
  const availability = useAvailability(slug, edition.isSuccess);
  // Bez kroku „zgłoszenie” (decyzja 2026‑09‑28): każda osoba z rodziny zalogowanego może się zapisywać.
  const registration = useQuery({
    queryKey: ['registration', slug],
    queryFn: () => apiGet(`/editions/${encodeURIComponent(slug)}/registration`, registrationViewSchema),
    enabled: !!me && edition.isSuccess,
  });
  const people = registration.data?.people ?? [];
  const [chosen, setChosen] = useState(() => readActivePerson(slug));
  // US-405 AC-2: domyślnie pierwsze dziecko, a gdy go nie ma – opiekun.
  const active = people.find((p) => p.participantId === chosen) ?? people.find((p) => !p.isGuardian) ?? people[0];
  const { controls } = useEnrollment(
    slug,
    active ? `/editions/${encodeURIComponent(slug)}/participants/${active.participantId}` : null,
    active?.firstName ?? '',
    availability.data?.version,
  );
  const desktop = useMediaQuery('(min-width: 62em)', true);

  if (edition.isPending) {
    return (
      <Center py="xl">
        <Loader aria-label="Ładowanie" />
      </Center>
    );
  }
  if (edition.error instanceof ApiError && edition.error.status === 404) return <NotFoundPage />;
  if (edition.isError) return <ErrorAlert error={edition.error} />;
  const e = edition.data;
  const detail = symbol ? (
    <DetailBody agenda={agenda.data} availability={availability.data} controls={controls} loading={agenda.isPending} symbol={symbol} loggedIn={!!me} />
  ) : null;

  if (symbol && !desktop) {
    return (
      <Stack gap="md">
        <Anchor component={Link} to={`/edycje/${e.slug}`} size="sm">
          {t.agenda.back}
        </Anchor>
        {detail}
      </Stack>
    );
  }

  // Trzy sekcje strony: wydarzenie (karta), rodzina i jej plan (karta), agenda (na tle strony – siatka potrzebuje szerokości).
  return (
    <Stack gap="lg">
      <section className={headerClasses.section} aria-label={e.name}>
        <Stack gap="sm">
          <RegistrationNotice edition={e} />
          <EditionHeader edition={e} />
          <SameDayEditions edition={e} editions={allEditions.data?.editions ?? []} />
        </Stack>
      </section>

      {e.status === 'finished' ? null : (
        <section className={headerClasses.section} aria-labelledby="family-section-title">
          <Stack gap="sm">
            <Title order={2} size="h4" id="family-section-title">
              {t.editions.familySection}
            </Title>
            {!me ? (
              <Button component={Link} to={`/logowanie?powrot=${encodeURIComponent(`/edycje/${e.slug}`)}`} w="fit-content">
                {t.editions.loginToRegister}
              </Button>
            ) : registration.isError ? (
              <ErrorAlert error={registration.error} />
            ) : registration.data ? (
              <PersonSwitcher
                people={registration.data.people}
                active={active}
                onChange={(id) => {
                  setChosen(id);
                  saveActivePerson(e.slug, id);
                }}
              />
            ) : (
              <Loader size="sm" aria-label="Ładowanie" />
            )}
            {active && controls && agenda.data ? (
              <PersonPlan
                agenda={agenda.data}
                controls={controls}
                name={`${active.firstName} ${active.lastName}`}
                onOpen={(a) => void navigate(`/edycje/${e.slug}/zajecia/${encodeURIComponent(a.symbol)}`)}
              />
            ) : null}
          </Stack>
        </section>
      )}

      <section aria-labelledby="agenda-section-title" className={headerClasses.agendaSection}>
        <Stack gap="sm">
          <Title order={2} size="h3" id="agenda-section-title">
            {t.agenda.title}
          </Title>
          {agenda.isPending ? <Loader size="sm" aria-label="Ładowanie" /> : null}
          {agenda.isError ? <ErrorAlert error={agenda.error} /> : null}
          {agenda.data ? (
            <AgendaView
              key={active?.participantId ?? 'all'}
              agenda={agenda.data}
              availability={availability.data}
              controls={controls}
              person={active}
              onOpen={(a) => void navigate(`/edycje/${e.slug}/zajecia/${encodeURIComponent(a.symbol)}`)}
            />
          ) : null}
        </Stack>
      </section>

      {/* Przy jednej edycji lista i tak prowadzi prosto tutaj – link tylko, gdy edycji jest więcej. */}
      {(allEditions.data?.editions.length ?? 0) > 1 ? (
        <Anchor component={Link} to="/" size="sm">
          {t.editions.allEditions}
        </Anchor>
      ) : null}

      <Modal
        opened={!!symbol}
        onClose={() => void navigate(`/edycje/${e.slug}`)}
        size="xl"
        radius="lg"
        padding="xl"
        overlayProps={{ blur: 3, backgroundOpacity: 0.4 }}
        title={
          <Text size="sm" c="dimmed" fw={600}>
            {t.agenda.details}
          </Text>
        }
      >
        {detail}
      </Modal>
    </Stack>
  );
}

/** Zwarty nagłówek w jednym wierszu – nazwa, termin, miasto i ikona mapy – żeby agenda była jak najwyżej. */
/**
 * Jedna konferencja w kilku miejscach tego samego dnia: skróty do pozostałych, żeby porównać agendy przed zapisem. Osoba zapisze
 * się tylko w jednym mieście, ale w kilku miejscach tego samego miasta – z przerwą na przejście (BR-17) – przy miejscu
 * z zapisami znacznik.
 */
function SameDayEditions({ edition, editions }: { edition: EditionSummary; editions: EditionSummary[] }) {
  const others = editions.filter((o) => o.date === edition.date && o.id !== edition.id);
  if (!others.length) return null;
  return (
    <Group gap="xs" wrap="wrap" style={{ rowGap: 4 }}>
      <Text size="sm" c="dimmed">
        {t.editions.otherCities}
      </Text>
      {others.map((o) => (
        <Button key={o.id} component={Link} to={`/edycje/${o.slug}`} size="compact-sm" variant={o.registered ? 'light' : 'default'} radius="xl">
          {editionPlaceLabel(o, editions)}
          {o.registered ? ` · ${t.editions.registered}` : ''}
        </Button>
      ))}
    </Group>
  );
}

/** Przed datą otwarcia zapisów: od kiedy można się zapisać (z wczesnym dostępem – że już teraz). */
function RegistrationNotice({ edition: e }: { edition: EditionSummary }) {
  if (e.status !== 'published' || e.registrationOpen) return null;
  const when = formatDateTime(e.registrationOpensAt);
  return (
    <Alert color={e.earlyAccess ? 'violet' : 'blue'} variant="light" radius="md" icon={<IconClock size={20} />} py="xs">
      {e.earlyAccess ? t.editions.notOpenYetEarly(when) : t.editions.notOpenYet(when)}
    </Alert>
  );
}

/** Kafelek z ikoną w kolorowym kółku, nazwą i podpisem; otwiera się w nowej karcie. */
function PlaceLink(p: { href: string; icon: ReactNode; title: string; hint: string; tone: 'map' | 'plan' | 'material' }) {
  return (
    <a href={p.href} target="_blank" rel="noopener noreferrer" className={`${headerClasses.place} ${headerClasses[p.tone]}`}>
      <span className={headerClasses.placeIcon}>{p.icon}</span>
      <span className={headerClasses.placeText}>
        <span className={headerClasses.placeTitle}>{p.title}</span>
        <span className={headerClasses.placeHint}>{p.hint}</span>
      </span>
      <IconExternalLink size={14} className={headerClasses.placeExternal} />
    </a>
  );
}

/** Kafelek jak `PlaceLink`, ale rozwija treść na stronie (informacje organizacyjne). */
function InfoTile(p: { open: boolean; onToggle: () => void; controls: string }) {
  return (
    <button
      type="button"
      onClick={p.onToggle}
      aria-expanded={p.open}
      aria-controls={p.controls}
      className={`${headerClasses.place} ${headerClasses.info}`}
    >
      <span className={headerClasses.placeIcon}>
        <IconInfoCircle size={20} />
      </span>
      <span className={headerClasses.placeText}>
        <span className={headerClasses.placeTitle}>{t.communication.info}</span>
        <span className={headerClasses.placeHint}>{p.open ? t.editions.infoHintOpen : t.editions.infoHint}</span>
      </span>
      <IconChevronDown size={14} className={`${headerClasses.placeExternal} ${p.open ? headerClasses.rotated : ''}`} />
    </button>
  );
}

function EditionHeader({ edition: e }: { edition: EditionSummary }) {
  const [infoOpen, setInfoOpen] = useState(false);
  const place = `${e.city}, ${e.address}`;
  const cal = calendarParts(e.date);
  const hasInfo = !!(e.infoHtml || e.noticeHtml);
  const materials = e.materials ?? [];
  return (
    <Stack gap="xs">
      {/* Nagłówek: kartka kalendarza z datą, nazwa edycji w kolorze edycji (bez niego – marki), kafelki z godzinami i miejscem. */}
      <div className={headerClasses.header}>
        <div className={headerClasses.calendar} title={formatDate(e.date)} aria-hidden="true">
          <div className={headerClasses.month}>{cal.month}</div>
          <div className={headerClasses.day}>{cal.day}</div>
          <div className={headerClasses.weekday}>
            {cal.weekday}
            <br />
            {cal.year}
          </div>
        </div>
        <Stack gap={8} miw={0}>
          <Title
            order={1}
            size="h2"
            className={headerClasses.name}
            style={e.accentColor ? ({ '--edition-accent': e.accentColor } as React.CSSProperties) : undefined}
          >
            {e.name}
          </Title>
          {/* Kartka kalendarza jest ozdobna (aria-hidden) – pełna data dla czytnika ekranu. */}
          <VisuallyHidden>{formatDate(e.date)}</VisuallyHidden>
          <Group gap="xs" wrap="wrap">
            <span className={headerClasses.chip}>
              <IconClock size={16} className={headerClasses.chipIcon} />
              {formatTime(e.startsAt)}–{formatTime(e.endsAt)}
            </span>
            <span className={headerClasses.chip} title={place}>
              <IconMapPin size={16} className={headerClasses.chipIcon} />
              {place}
            </span>
          </Group>
          {/* Mapa dojazdu, plan budynku i informacje organizacyjne jako wyraźne kafelki – tylko te, które są w danych edycji. */}
          {e.mapUrl || e.floorPlanUrl || hasInfo || materials.length ? (
            <Group gap="xs" wrap="wrap">
              {e.mapUrl ? (
                <PlaceLink href={e.mapUrl} icon={<IconRoute size={20} />} title={t.editions.map} hint={t.editions.mapHint} tone="map" />
              ) : null}
              {e.floorPlanUrl ? (
                <PlaceLink
                  href={e.floorPlanUrl}
                  icon={<IconFloorPlan size={20} />}
                  title={t.editions.floorPlan}
                  hint={t.editions.floorPlanHint}
                  tone="plan"
                />
              ) : null}
              {/* PRD §10 pkt 40: materiały edycji (np. labirynt zagadek) – panel admina edycji. */}
              {materials.map((m) => (
                <PlaceLink
                  key={m.id}
                  href={m.url}
                  icon={<IconFileText size={20} />}
                  title={m.title}
                  hint={m.hint ?? t.materials.defaultHint}
                  tone="material"
                />
              ))}
              {hasInfo ? <InfoTile open={infoOpen} onToggle={() => setInfoOpen((o) => !o)} controls="edition-info" /> : null}
            </Group>
          ) : null}
        </Stack>
      </div>
      {/* US-106 AC-2: informacje organizacyjne i zastrzeżenia (HTML oczyszczony przez API przy zapisie) – rozwijane kafelkiem. */}
      {hasInfo ? (
        <>
          <Collapse expanded={infoOpen} id="edition-info">
            <Paper withBorder p="md" radius="md">
              <Stack gap="xs">
                {e.infoHtml ? (
                  <Typography>
                    <div dangerouslySetInnerHTML={{ __html: e.infoHtml }} />
                  </Typography>
                ) : null}
                {e.noticeHtml ? (
                  <Typography>
                    <div dangerouslySetInnerHTML={{ __html: e.noticeHtml }} />
                  </Typography>
                ) : null}
              </Stack>
            </Paper>
          </Collapse>
        </>
      ) : null}
    </Stack>
  );
}

function DetailBody({
  agenda,
  availability,
  controls,
  loading,
  symbol,
  loggedIn,
}: {
  agenda: Agenda | undefined;
  availability: Availability | undefined;
  controls: EnrollmentControls | null;
  loading: boolean;
  symbol: string;
  loggedIn: boolean;
}) {
  if (loading) return <Loader size="sm" aria-label="Ładowanie" />;
  const activity = agenda && withAvailability(agenda.activities, availability).find((a) => a.symbol.toLowerCase() === symbol.toLowerCase());
  if (!agenda || !activity) return <Text>{t.agenda.notFound}</Text>;
  return <ActivityDetail agenda={agenda} activity={activity} loggedIn={loggedIn} controls={controls} availability={availability} />;
}

/**
 * US-408 AC-1…AC-4: osoby z rodziny (zapamiętany wybór) i skrót do „Mojej rodziny”, żeby dodać osobę – dodana osoba od razu
 * jest uczestnikiem (bez kroku „zgłoszenie”, decyzja 2026‑09‑28).
 */
function PersonSwitcher({
  people,
  active,
  onChange,
}: {
  people: RegistrationPerson[];
  active: RegistrationPerson | undefined;
  onChange: (participantId: string) => void;
}) {
  const location = useLocation();
  return (
    <PersonTabs
      people={people}
      value={active?.participantId}
      onChange={onChange}
      rightSection={
        <Button component={Link} to={`/rodzina?powrot=${encodeURIComponent(location.pathname)}`} variant="subtle" size="xs">
          {t.editions.addPerson}
        </Button>
      }
    />
  );
}
